import React, { useState, useEffect, useCallback, useMemo } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Chip, Stack, Button, IconButton, Tooltip,
  Divider, List, ListItemButton, ListItemIcon, ListItemText,
  Skeleton, Popover, FormGroup, FormControlLabel, Checkbox, Collapse,
} from "@mui/material";
import AllInboxIcon        from "@mui/icons-material/AllInbox";
import StarIcon            from "@mui/icons-material/Star";
import StarBorderIcon      from "@mui/icons-material/StarBorder";
import BoltIcon            from "@mui/icons-material/Bolt";
import NewReleasesIcon     from "@mui/icons-material/NewReleases";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import RefreshIcon         from "@mui/icons-material/Refresh";
import OpenInNewIcon       from "@mui/icons-material/OpenInNew";
import VisibilityOffIcon   from "@mui/icons-material/VisibilityOff";
import DoneAllIcon         from "@mui/icons-material/DoneAll";
import FolderIcon          from "@mui/icons-material/Folder";
import FolderOpenIcon      from "@mui/icons-material/FolderOpen";
import RssFeedIcon         from "@mui/icons-material/RssFeed";
import TuneIcon            from "@mui/icons-material/Tune";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import ExpandLessIcon      from "@mui/icons-material/ExpandLess";
import CircleIcon          from "@mui/icons-material/Circle";
import { isAutheticated }  from "../auth/helper";
import { getArticles, getFeedGroups, getFeeds } from "./helper/adminapicall";

const FRESH_MS      = 2 * 60 * 60 * 1000;
const LS_READ       = "feeditems_read";
const LS_STARRED    = "feeditems_starred";
const LS_COLS       = "feeditems_cols";
const LIMIT_PAGE    = 50;
const LIMIT_BATCH   = 100;
const FEEDS_PER_PAGE = 20;   // feeds loaded per "Load more" click

const CLASS_FIELDS = [
  { key: "content_type",  label: "Content Type", array: false },
  { key: "format",        label: "Format",        array: false },
  { key: "asset_classes", label: "Asset Classes", array: true  },
  { key: "sectors",       label: "Sectors",       array: true  },
  { key: "topic_codes",   label: "Topic Codes",   array: true  },
  { key: "tags",          label: "Tags",          array: true  },
  { key: "regions",       label: "Regions",       array: true  },
  { key: "continents",    label: "Continents",    array: true  },
];

const loadSet = (key) => {
  try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); }
  catch { return new Set(); }
};
const saveSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
};
const loadArr = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); }
  catch { return []; }
};

const timeAgo = (d) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60000)    return "just now";
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// groupFeeds entry shape: { feeds: [], total: 0, nextPage: 2, loading: false }
const EMPTY_GF = { feeds: [], total: 0, nextPage: 2, loading: false };

const FeedItems = () => {
  const { admin, token } = isAutheticated();

  const [readSet,    setReadSet]    = useState(() => loadSet(LS_READ));
  const [starredSet, setStarredSet] = useState(() => loadSet(LS_STARRED));
  const [colCfg,     setColCfg]    = useState(() => loadArr(LS_COLS));
  const [groups,     setGroups]     = useState([]);
  const [articles,   setArticles]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  // expandedGroups: Set<groupId>
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // groupFeeds: { [groupId]: { feeds, total, nextPage, loading } }
  const [groupFeeds, setGroupFeeds] = useState({});

  // selection: { type, value? }
  const [selection, setSelection] = useState({ type: "all" });

  useEffect(() => {
    getFeedGroups(admin._id, token).then(d => setGroups(Array.isArray(d) ? d : []));
  }, [admin._id, token]);

  const isBatch = ["starred", "unread", "fresh"].includes(selection.type);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set("page",  String(page + 1));
    qs.set("limit", String(isBatch ? LIMIT_BATCH : LIMIT_PAGE));
    if (selection.type === "group")    qs.set("group_id",    selection.value);
    if (selection.type === "feed")     qs.set("feed_id",     selection.value);
    if (selection.type === "breaking") qs.set("is_breaking", "true");
    if (selection.type === "fresh")
      qs.set("published_from", new Date(Date.now() - FRESH_MS).toISOString());

    getArticles(admin._id, token, "?" + qs.toString())
      .then(d => {
        setArticles(Array.isArray(d?.articles) ? d.articles : []);
        setTotal(d?.total ?? 0);
      })
      .finally(() => setLoading(false));
  }, [admin._id, token, page, selection, isBatch]);

  useEffect(() => { load(); }, [load]);

  // ── Feed loading helpers ──────────────────────────────────────────────────────
  const fetchGroupFeeds = useCallback((groupId, pageNum, append = false) => {
    setGroupFeeds(p => ({
      ...p,
      [groupId]: { ...(p[groupId] || EMPTY_GF), loading: true },
    }));
    const qs = `?group_id=${groupId}&page=${pageNum}&limit=${FEEDS_PER_PAGE}`;
    getFeeds(admin._id, token, qs)
      .then(d => {
        const incoming = Array.isArray(d?.feeds) ? d.feeds : Array.isArray(d) ? d : [];
        const total    = d?.total ?? incoming.length;
        setGroupFeeds(p => {
          const prev = p[groupId] || EMPTY_GF;
          return {
            ...p,
            [groupId]: {
              feeds:    append ? [...prev.feeds, ...incoming] : incoming,
              total,
              nextPage: pageNum + 1,
              loading:  false,
            },
          };
        });
      })
      .catch(() => {
        setGroupFeeds(p => ({
          ...p,
          [groupId]: { ...(p[groupId] || EMPTY_GF), loading: false },
        }));
      });
  }, [admin._id, token]);

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
        // First open — load page 1
        if (!groupFeeds[groupId]) fetchGroupFeeds(groupId, 1, false);
      }
      return next;
    });
  }, [groupFeeds, fetchGroupFeeds]);

  const loadMoreFeeds = useCallback((groupId) => {
    const gf = groupFeeds[groupId];
    if (!gf || gf.loading) return;
    fetchGroupFeeds(groupId, gf.nextPage, true);
  }, [groupFeeds, fetchGroupFeeds]);

  // ── Article helpers ───────────────────────────────────────────────────────────
  const displayArticles = useMemo(() => {
    if (selection.type === "starred") return articles.filter(a => starredSet.has(a._id));
    if (selection.type === "unread")  return articles.filter(a => !readSet.has(a._id));
    return articles;
  }, [articles, selection, starredSet, readSet]);

  const markRead = useCallback((id) => {
    setReadSet(prev => {
      const next = new Set(prev); next.add(id);
      saveSet(LS_READ, next); return next;
    });
  }, []);

  const toggleStar = useCallback((id) => {
    setStarredSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveSet(LS_STARRED, next); return next;
    });
  }, []);

  const markAllRead = () => {
    setReadSet(prev => {
      const next = new Set(prev);
      articles.forEach(a => next.add(a._id));
      saveSet(LS_READ, next); return next;
    });
  };

  const toggleCol = (key) => {
    const next = colCfg.includes(key) ? colCfg.filter(k => k !== key) : [...colCfg, key];
    setColCfg(next);
    try { localStorage.setItem(LS_COLS, JSON.stringify(next)); } catch {}
  };

  const select = (type, value) => { setSelection({ type, value }); setPage(0); };

  const unreadInBatch  = useMemo(() => articles.filter(a => !readSet.has(a._id)).length,   [articles, readSet]);
  const starredInBatch = useMemo(() => articles.filter(a => starredSet.has(a._id)).length, [articles, starredSet]);

  const sidebarCategories = [
    { type: "all",      label: "All Items", icon: <AllInboxIcon fontSize="small" /> },
    { type: "unread",   label: "Unread",    icon: <MarkEmailUnreadIcon fontSize="small" />, badge: unreadInBatch },
    { type: "fresh",    label: "Fresh",     icon: <NewReleasesIcon fontSize="small" sx={{ color: "success.main" }} /> },
    { type: "breaking", label: "Breaking",  icon: <BoltIcon fontSize="small" sx={{ color: "error.main" }} /> },
    { type: "starred",  label: "Starred",   icon: <StarIcon fontSize="small" sx={{ color: "warning.main" }} />, badge: starredInBatch },
  ];

  const getClassChips = (a) => {
    const chips = [];
    CLASS_FIELDS.forEach(({ key, array }) => {
      if (!colCfg.includes(key)) return;
      if (array) {
        (a[key] || []).forEach(v => chips.push({ key: `${key}:${v}`, label: v }));
      } else if (a[key]) {
        chips.push({ key: `${key}:${a[key]}`, label: a[key] });
      }
    });
    return chips;
  };

  const pageTitle = useMemo(() => {
    if (selection.type === "feed") {
      for (const gId of Object.keys(groupFeeds)) {
        const f = (groupFeeds[gId]?.feeds || []).find(f => f._id === selection.value);
        if (f) return f.title || f.source_name || "Feed";
      }
      return "Feed";
    }
    return {
      all: "All Items", fresh: "Fresh Articles", breaking: "Breaking News",
      starred: "Starred", unread: "Unread",
      group: groups.find(g => g._id === selection.value)?.name || "Group",
    }[selection.type] || "Articles";
  }, [selection, groups, groupFeeds]);

  return (
    <Base>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>

        {/* ── Sidebar ── */}
        <Paper elevation={0} sx={{
          width: 230, flexShrink: 0,
          border: "1px solid", borderColor: "divider", borderRadius: 2,
          overflow: "hidden", position: "sticky", top: 16,
          maxHeight: "calc(100vh - 100px)", overflowY: "auto",
          scrollbarWidth: "thin",
        }}>
          <Box sx={{
            px: 2, py: 1.25,
            borderBottom: "1px solid", borderColor: "divider",
            position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <RssFeedIcon sx={{ fontSize: 13, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.8 }}>
                Feeds
              </Typography>
            </Box>
          </Box>

          {/* Category shortcuts */}
          <List dense disablePadding>
            {sidebarCategories.map(item => (
              <ListItemButton
                key={item.type}
                selected={selection.type === item.type}
                onClick={() => select(item.type)}
                sx={{ py: 0.65, px: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 26 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    variant: "body2", fontSize: "0.82rem",
                    fontWeight: selection.type === item.type ? 600 : 400,
                  }}
                />
                {item.badge > 0 && (
                  <Chip label={item.badge} size="small" color="primary"
                    sx={{ height: 17, fontSize: "0.62rem", fontWeight: 600 }} />
                )}
              </ListItemButton>
            ))}
          </List>

          {/* Groups — expandable with paginated feed list */}
          {groups.length > 0 && (
            <>
              <Divider />
              <Box sx={{ px: 2, py: 0.75 }}>
                <Typography variant="caption" fontWeight={700} color="text.disabled"
                  sx={{ textTransform: "uppercase", fontSize: "0.62rem", letterSpacing: 0.8 }}>
                  Groups
                </Typography>
              </Box>
              <List dense disablePadding sx={{ pb: 1 }}>
                {groups.map(g => {
                  const isExpanded = expandedGroups.has(g._id);
                  const isGroupSel = selection.type === "group" && selection.value === g._id;
                  const gf         = groupFeeds[g._id];
                  const hasMore    = gf && gf.feeds.length < gf.total;
                  const remaining  = gf ? gf.total - gf.feeds.length : 0;

                  return (
                    <React.Fragment key={g._id}>
                      {/* Group header row */}
                      <ListItemButton selected={isGroupSel} sx={{ py: 0.55, px: 1.5, pr: 0.5 }}>
                        <ListItemIcon
                          sx={{ minWidth: 24 }}
                          onClick={() => select("group", g._id)}
                        >
                          {isExpanded
                            ? <FolderOpenIcon sx={{ color: "primary.main", fontSize: 15 }} />
                            : <FolderIcon sx={{ color: "text.disabled", fontSize: 15 }} />}
                        </ListItemIcon>

                        <ListItemText
                          onClick={() => select("group", g._id)}
                          primary={g.name}
                          primaryTypographyProps={{
                            variant: "body2", fontSize: "0.8rem", noWrap: true,
                            fontWeight: isGroupSel ? 600 : 400,
                          }}
                        />

                        {/* Expand/collapse */}
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); toggleGroup(g._id); }}
                          sx={{ color: "text.disabled", flexShrink: 0 }}
                        >
                          {isExpanded
                            ? <ExpandLessIcon sx={{ fontSize: 15 }} />
                            : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
                        </IconButton>
                      </ListItemButton>

                      {/* Feed sub-list */}
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding>
                          {/* Loading skeletons on first load */}
                          {!gf || (gf.loading && gf.feeds.length === 0)
                            ? [1, 2, 3].map(i => (
                                <Box key={i} sx={{ pl: 4.5, py: 0.6, pr: 2 }}>
                                  <Skeleton width="80%" height={13} />
                                </Box>
                              ))
                            : gf.feeds.length === 0
                              ? (
                                <Box sx={{ pl: 4.5, py: 0.75 }}>
                                  <Typography variant="caption" color="text.disabled">No feeds</Typography>
                                </Box>
                              )
                              : gf.feeds.map(f => {
                                  const isFeedSel = selection.type === "feed" && selection.value === f._id;
                                  return (
                                    <ListItemButton
                                      key={f._id}
                                      selected={isFeedSel}
                                      onClick={() => select("feed", f._id)}
                                      sx={{ pl: 3.5, py: 0.35, pr: 1 }}
                                    >
                                      <ListItemIcon sx={{ minWidth: 16 }}>
                                        <CircleIcon sx={{
                                          fontSize: 6,
                                          color: f.is_active ? "success.main" : "error.main",
                                        }} />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={f.title || f.source_name || f.url}
                                        primaryTypographyProps={{
                                          variant: "caption",
                                          fontSize: "0.74rem",
                                          noWrap: true,
                                          fontWeight: isFeedSel ? 600 : 400,
                                          color: isFeedSel ? "text.primary" : "text.secondary",
                                        }}
                                      />
                                    </ListItemButton>
                                  );
                                })
                          }

                          {/* Load more feeds */}
                          {hasMore && (
                            <Box sx={{ pl: 3.5, py: 0.5 }}>
                              <Button
                                size="small"
                                onClick={() => loadMoreFeeds(g._id)}
                                disabled={gf?.loading}
                                sx={{ fontSize: "0.68rem", py: 0.25, px: 0.75, minWidth: 0 }}
                              >
                                {gf?.loading
                                  ? "Loading…"
                                  : `Load more (${remaining} remaining)`}
                              </Button>
                            </Box>
                          )}

                          {/* Total count */}
                          {gf && !gf.loading && gf.total > 0 && (
                            <Box sx={{ pl: 3.5, pb: 0.5 }}>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                                {gf.feeds.length} of {gf.total} feeds
                              </Typography>
                            </Box>
                          )}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </List>
            </>
          )}
        </Paper>

        {/* ── Article list ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Toolbar */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} display="inline">{pageTitle}</Typography>
              {!isBatch && total > 0 && (
                <Typography variant="body2" color="text.disabled" display="inline" sx={{ ml: 1 }}>
                  {total.toLocaleString()} articles
                </Typography>
              )}
              {isBatch && displayArticles.length > 0 && (
                <Typography variant="body2" color="text.disabled" display="inline" sx={{ ml: 1 }}>
                  {displayArticles.length} shown
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined"
                startIcon={<DoneAllIcon sx={{ fontSize: 14 }} />}
                onClick={markAllRead} sx={{ fontSize: "0.75rem" }}>
                Mark all read
              </Button>
              <Tooltip title="Show classification fields">
                <IconButton
                  size="small"
                  onClick={e => setSettingsAnchor(e.currentTarget)}
                  color={colCfg.length > 0 ? "primary" : "default"}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* Classification settings popover */}
          <Popover
            open={Boolean(settingsAnchor)}
            anchorEl={settingsAnchor}
            onClose={() => setSettingsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ p: 2, minWidth: 220 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Show classification
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Extra fields shown as chips on each article
              </Typography>
              <FormGroup>
                {CLASS_FIELDS.map(f => (
                  <FormControlLabel
                    key={f.key}
                    label={<Typography variant="body2">{f.label}</Typography>}
                    control={<Checkbox size="small" checked={colCfg.includes(f.key)} onChange={() => toggleCol(f.key)} />}
                    sx={{ mb: -0.5 }}
                  />
                ))}
              </FormGroup>
              {colCfg.length > 0 && (
                <Button size="small" sx={{ mt: 1.5 }} onClick={() => {
                  setColCfg([]); try { localStorage.setItem(LS_COLS, "[]"); } catch {}
                }}>Clear all</Button>
              )}
            </Box>
          </Popover>

          {/* Article rows */}
          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Skeleton width="72%" height={18} />
                  <Skeleton width="38%" height={14} sx={{ mt: 0.5 }} />
                </Box>
              ))
            ) : displayArticles.length === 0 ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <RssFeedIcon sx={{ fontSize: 36, color: "text.disabled", opacity: 0.3 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No articles here yet
                </Typography>
              </Box>
            ) : (
              displayArticles.map((a, idx) => {
                const isRead     = readSet.has(a._id);
                const isStarred  = starredSet.has(a._id);
                const isFresh    = Date.now() - new Date(a.fetched_at).getTime() < FRESH_MS;
                const isLast     = idx === displayArticles.length - 1;
                const classChips = getClassChips(a);

                return (
                  <Box
                    key={a._id}
                    onClick={() => markRead(a._id)}
                    sx={{
                      display: "flex", alignItems: "flex-start", gap: 0.5,
                      px: 1, py: 1,
                      borderBottom: isLast ? "none" : "1px solid", borderColor: "divider",
                      bgcolor: isRead ? "transparent" : "action.selected",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                      transition: "background-color 0.12s",
                    }}
                  >
                    {/* Star */}
                    <IconButton size="small"
                      onClick={e => { e.stopPropagation(); toggleStar(a._id); }}
                      sx={{ mt: 0.1, flexShrink: 0, color: isStarred ? "warning.main" : "action.disabled" }}>
                      {isStarred ? <StarIcon sx={{ fontSize: 15 }} /> : <StarBorderIcon sx={{ fontSize: 15 }} />}
                    </IconButton>

                    {/* Unread dot */}
                    <Box sx={{ mt: 0.95, mr: 0.5, flexShrink: 0 }}>
                      <Box sx={{
                        width: 7, height: 7, borderRadius: "50%",
                        bgcolor: isRead ? "transparent" : "primary.main",
                        transition: "background-color 0.12s",
                      }} />
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
                        {isFresh && !isRead && (
                          <Chip label="FRESH" size="small" color="success"
                            sx={{ height: 15, fontSize: "0.58rem", fontWeight: 700, px: 0.25 }} />
                        )}
                        {a.is_breaking && (
                          <Chip label="BREAKING" size="small" color="error"
                            sx={{ height: 15, fontSize: "0.58rem", fontWeight: 700, px: 0.25 }} />
                        )}
                        <Typography
                          component="a"
                          href={a.url || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => { e.stopPropagation(); markRead(a._id); }}
                          variant="body2"
                          sx={{
                            fontWeight: isRead ? 400 : 600,
                            color: isRead ? "text.secondary" : "text.primary",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline", color: "primary.main" },
                            lineHeight: 1.45,
                          }}
                        >
                          {a.clean_headline || a.headline}
                        </Typography>
                      </Box>

                      {/* Meta */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.25, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.72rem" }}>
                          {a.source_name || a.source_code || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">·</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.72rem" }}>
                          {timeAgo(a.published_at)}
                        </Typography>
                        {a.group_id?.name && (
                          <>
                            <Typography variant="caption" color="text.disabled">·</Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.72rem" }}>
                              {a.group_id.name}
                            </Typography>
                          </>
                        )}
                        {(a.symbols || []).slice(0, 3).map(s => (
                          <Chip key={s} label={s} size="small" variant="outlined"
                            sx={{ height: 15, fontSize: "0.6rem", px: 0.25 }} />
                        ))}
                      </Box>

                      {/* Classification chips */}
                      {classChips.length > 0 && (
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.4, flexWrap: "wrap" }}>
                          {classChips.map(c => (
                            <Chip key={c.key} label={c.label} size="small"
                              sx={{ height: 16, fontSize: "0.6rem", bgcolor: "action.disabledBackground" }} />
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" sx={{ flexShrink: 0, ml: 0.5 }}>
                      {!isRead && (
                        <Tooltip title="Mark as read">
                          <IconButton size="small"
                            onClick={e => { e.stopPropagation(); markRead(a._id); }}
                            sx={{ color: "action.disabled" }}>
                            <VisibilityOffIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {a.url && (
                        <Tooltip title="Open article">
                          <IconButton size="small" component="a" href={a.url}
                            target="_blank" rel="noopener noreferrer"
                            onClick={e => { e.stopPropagation(); markRead(a._id); }}
                            sx={{ color: "action.disabled" }}>
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                );
              })
            )}
          </Paper>

          {/* Pagination */}
          {!isBatch && total > LIMIT_PAGE && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2, gap: 1.5 }}>
              <Button size="small" variant="outlined" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Prev
              </Button>
              <Typography variant="body2" color="text.secondary">
                {page + 1} / {Math.ceil(total / LIMIT_PAGE)}
              </Typography>
              <Button size="small" variant="outlined"
                disabled={(page + 1) * LIMIT_PAGE >= total}
                onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Base>
  );
};

export default FeedItems;
